import {
  MIN_SECTION_DURATION_SECONDS,
  SECTION_CADENCE_MIN_MAX_STEP,
} from '../configs/sections-editor/default-section.config';
import { DEFAULT_WORKOUT_SECTION_MIN_MAX_STEP } from '../configs/sections-editor/workout-mode-initial-min-max-avr.config';
import {
  ZWO_FREE_RIDE_POWER,
  ZWO_RAMP_STEP_SECONDS,
  ZWO_SPORT_TYPES,
  ZWO_ZONE_POWER,
} from '../configs/sections-editor/zwo-import.config';
import { MinMaxStep, SectionModelType } from '../types/section.type';
import { WorkoutSportType } from '../types/sport.type';
import {
  ZwoImportError,
  ZwoImportMode,
  ZwoImportResult,
  ZwoImportWarning,
  ZwoImportWarningCode,
} from '../types/zwo.type';
import { generateSectionId } from './default-section';
import { durationToSeconds, secondsToDuration } from './duration.utils';
import { snapToStep } from './sections-editor.utils';

type ZwoBlock = { tag: string; attributes: Record<string, number> };
type ZwoStep = { seconds: number; power: number; cadence?: number };
type Warn = (code: ZwoImportWarningCode) => void;

/** Attributs Zwift marquant un bloc de test. */
const TEST_ATTRIBUTES: string[] = ['ftptest', 'ramptest', 'failthresholdduration'];

/**
 * Convertit le contenu d'un fichier Zwift `.zwo` en sections de workout Kinomap.
 * Tout ce qui est approxime ou ignore est remonte dans `warnings`.
 * @throws ZwoImportError si le fichier ne peut pas etre importe.
 */
export const convertZwoToWorkout = (content: string): ZwoImportResult => {
  const workoutFile: Element = parseZwoFile(content);

  const sport: WorkoutSportType | undefined =
    ZWO_SPORT_TYPES[readText(workoutFile, 'sporttype')?.toLowerCase() ?? 'bike'];
  if (!sport) throw new ZwoImportError('unsupported_sport');

  const durationType: string = readText(workoutFile, 'durationtype')?.toLowerCase() ?? 'time';
  if (durationType !== 'time') throw new ZwoImportError('unsupported_duration_type');

  const ftpOverride: number = Number(readText(workoutFile, 'ftpoverride')) || 0;
  const mode: ZwoImportMode = ftpOverride > 0 ? 'watts' : 'ftp';
  const warnings: ZwoImportWarning[] = [];

  const sections: SectionModelType[] = readBlocks(workoutFile).flatMap(
    (block: ZwoBlock, blockIndex: number) => {
      const warn: Warn = (code: ZwoImportWarningCode) => {
        const exists: boolean = warnings.some(
          (warning: ZwoImportWarning) => warning.code === code && warning.blockIndex === blockIndex,
        );
        if (!exists) warnings.push({ code, block: block.tag, blockIndex });
      };

      const steps: ZwoStep[] = convertBlockToSteps(block, warn);
      return mergeEqualSections(
        steps.map((step: ZwoStep) => convertStepToSection(step, mode, ftpOverride, warn)),
      );
    },
  );

  if (!sections.length) throw new ZwoImportError('empty_workout');

  return {
    name: readText(workoutFile, 'name'),
    description: readText(workoutFile, 'description'),
    author: readText(workoutFile, 'author'),
    sport,
    mode,
    sections,
    warnings,
  };
};

function convertBlockToSteps(block: ZwoBlock, warn: Warn): ZwoStep[] {
  const { attributes } = block;
  const seconds: number = attributes['duration'] ?? 0;
  const cadence: number | undefined = readCadence(attributes);

  if (TEST_ATTRIBUTES.some((name: string) => attributes[name] > 0)) warn('test_ignored');

  switch (block.tag.toLowerCase()) {
    case 'steadystate':
    case 'solidstate':
      return [{ seconds, power: readPower(attributes, 'power', 'zone', warn), cadence }];

    case 'warmup':
    case 'cooldown':
    case 'ramp': {
      const start: number = attributes['powerlow'] ?? readPower(attributes, 'power', 'zone', warn);
      const end: number = attributes['powerhigh'] ?? start;
      return splitRamp(seconds, start, end, warn).map((step: ZwoStep) => ({ ...step, cadence }));
    }

    case 'intervalst': {
      const on: ZwoStep = {
        seconds: attributes['onduration'] ?? 0,
        power: readPower(attributes, 'onpower', 'poweronzone', warn),
        cadence,
      };
      const off: ZwoStep = {
        seconds: attributes['offduration'] ?? 0,
        power: readPower(attributes, 'offpower', 'poweroffzone', warn),
        cadence: attributes['cadenceresting'] > 0 ? attributes['cadenceresting'] : undefined,
      };
      const cycle: ZwoStep[] = off.seconds > 0 ? [on, off] : [on];
      const repeat: number = Math.max(1, Math.round(attributes['repeat'] ?? 1));
      return Array.from({ length: repeat }, () => cycle).flat();
    }

    case 'freeride':
    case 'maxeffort':
      warn('free_ride_converted');
      return [{ seconds, power: ZWO_FREE_RIDE_POWER, cadence }];

    default:
      warn('unsupported_block');
      return [];
  }
}

/** Cadence cible d'un bloc, en rpm : `Cadence`, sinon le milieu de `CadenceLow` / `CadenceHigh`. */
function readCadence(attributes: Record<string, number>): number | undefined {
  if (attributes['cadence'] > 0) return attributes['cadence'];
  if (attributes['cadencelow'] > 0 && attributes['cadencehigh'] > 0) {
    return (attributes['cadencelow'] + attributes['cadencehigh']) / 2;
  }
  return undefined;
}

/**
 * Puissance d'un bloc en ratio de FTP.
 */
function readPower(
  attributes: Record<string, number>,
  powerAttribute: string,
  zoneAttribute: string,
  warn: Warn,
): number {
  const power: number | undefined = attributes[powerAttribute];
  if (power !== undefined) return power;

  const zone: number | undefined = attributes[zoneAttribute];

  if (zone !== undefined) {
    warn('zone_converted');
    return ZWO_ZONE_POWER[Math.min(6, Math.max(1, Math.round(zone)))];
  }

  warn('free_ride_converted');
  return ZWO_FREE_RIDE_POWER;
}

function splitRamp(seconds: number, start: number, end: number, warn: Warn): ZwoStep[] {
  const count: number =
    start === end ? 1 : Math.max(1, Math.round(seconds / ZWO_RAMP_STEP_SECONDS));
  if (count > 1) warn('ramp_split');

  return Array.from({ length: count }, (_: unknown, index: number) => ({
    seconds: Math.round(((index + 1) * seconds) / count) - Math.round((index * seconds) / count),
    power: start + ((end - start) * (index + 0.5)) / count,
  }));
}

function convertStepToSection(
  step: ZwoStep,
  mode: ZwoImportMode,
  ftpOverride: number,
  warn: Warn,
): SectionModelType {
  const { min, max }: MinMaxStep = DEFAULT_WORKOUT_SECTION_MIN_MAX_STEP[mode];
  const rawValue: number = mode === 'watts' ? step.power * ftpOverride : step.power * 100;
  if (rawValue < min || rawValue > max) warn('value_clamped');
  // Arrondi a l'unite et non au pas de l'editeur (5 % en FTP) : 0.586 donne 59 %, pas 60 %.
  const value: number = snapToStep(rawValue, min, max, 1);

  const seconds: number = Math.round(step.seconds);
  if (seconds < MIN_SECTION_DURATION_SECONDS) warn('duration_adjusted');

  const cadenceBounds: MinMaxStep = SECTION_CADENCE_MIN_MAX_STEP;
  if (step.cadence !== undefined && step.cadence > cadenceBounds.max) warn('value_clamped');
  const cadence: number | undefined =
    step.cadence === undefined
      ? undefined
      : snapToStep(step.cadence, cadenceBounds.min, cadenceBounds.max, cadenceBounds.step);

  return {
    id: generateSectionId(),
    data: mode === 'watts' ? { watts: value } : { ftp: value },
    duration: secondsToDuration(Math.max(seconds, MIN_SECTION_DURATION_SECONDS)),
    cadence,
  };
}

function mergeEqualSections(sections: SectionModelType[]): SectionModelType[] {
  const merged: SectionModelType[] = [];

  sections.forEach((section: SectionModelType) => {
    const previous: SectionModelType | undefined = merged.at(-1);
    const isSameTarget: boolean =
      JSON.stringify(previous?.data) === JSON.stringify(section.data) &&
      previous?.cadence === section.cadence;
    if (previous && isSameTarget) {
      previous.duration = secondsToDuration(
        durationToSeconds(previous.duration) + durationToSeconds(section.duration),
      );
    } else {
      merged.push(section);
    }
  });

  return merged;
}

function parseZwoFile(content: string): Element {
  const doc: XMLDocument = new DOMParser().parseFromString(content, 'text/xml');
  const workoutFile: Element = doc.documentElement;
  const isValid: boolean =
    !doc.getElementsByTagName('parsererror').length &&
    workoutFile?.tagName.toLowerCase() === 'workout_file';
  if (!isValid) throw new ZwoImportError('invalid_file');
  return workoutFile;
}

function findChild(parent: Element, name: string): Element | undefined {
  return Array.from(parent.children).find((child: Element) => child.tagName.toLowerCase() === name);
}

function readText(workoutFile: Element, name: string): string | undefined {
  return findChild(workoutFile, name)?.textContent?.trim() || undefined;
}

function readBlocks(workoutFile: Element): ZwoBlock[] {
  const blocks: Element[] = Array.from(findChild(workoutFile, 'workout')?.children ?? []);

  return blocks.map((element: Element) => {
    const attributes: Record<string, number> = {};
    Array.from(element.attributes).forEach((attribute: Attr) => {
      const value: number = Number(attribute.value);
      if (attribute.value.trim() && Number.isFinite(value)) {
        attributes[attribute.name.toLowerCase()] = value;
      }
    });
    return { tag: element.tagName, attributes };
  });
}
